<script setup lang="ts">
import { computed, getCurrentInstance, onActivated, onDeactivated, onBeforeUnmount, ref, toRef, watch } from 'vue'
import { usePlayerStore, type PlayerTrack } from '~/stores/player'
import MmMenu from '../base/MmMenu.vue'
import MmIconButton from '../base/MmIconButton.vue'
import MmIcon from '../base/MmIcon.vue'
import FeedShare from './FeedShare.vue'
import { useSongLike } from './useSongLike'
import PublishDialog from '../creator/PublishDialog.vue'
import type { LibrarySong, PublishTarget } from '../creator/types'
type RowSong = PlayerTrack & { user_id?: string; task_id?: string; is_public?: boolean; visibility?: string; publish_status?: string }
const props = withDefaults(defineProps<{ song: RowSong; playerMenu?: boolean; showRemix?: boolean; inlineActions?: boolean }>(), { showRemix: true, inlineActions: false })
const emit = defineEmits<{ remix: [song: PlayerTrack] }>()
const instance = getCurrentInstance()
const { t } = useI18n()
const player = usePlayerStore()
const { saveMusicData } = useMusicCreate()
const router = useRouter()
const { isAuthenticated, userAccount } = useAuthState()
const open = ref(false), share = ref(false)
const publishTarget = ref<PublishTarget | null>(null)
const active = ref(true)
// Public generated-song payloads expose the backend task owner and task ID.
// Never infer ownership from a display name or Clerk's external user ID.
const canPublish = computed(() => props.inlineActions && active.value && !!props.song.id && isAuthenticated.value
  && typeof props.song.user_id === 'string' && !!props.song.user_id
  && props.song.user_id === userAccount.value?.id
  && typeof props.song.task_id === 'string' && !!props.song.task_id.trim())
const published = computed(() => props.song.visibility === 'public' || props.song.is_public === true || props.song.publish_status === 'published')
const publishLabel = computed(() => `${t(published.value ? 'creator.editPublishing' : 'creator.publish')} · ${props.song.title || t('copyright.untitled')}`)
function closeActions() { open.value = false; share.value = false; publishTarget.value = null }
watch([() => props.song.id, () => props.song.user_id, () => props.song.task_id, () => userAccount.value?.id, isAuthenticated], closeActions, { flush: 'sync' })
onActivated(() => { active.value = true })
onDeactivated(() => { active.value = false; closeActions() })
onBeforeUnmount(() => { active.value = false; closeActions() })
function openPublishing() {
  if (!canPublish.value) return
  publishTarget.value = { song: props.song as LibrarySong, taskId: props.song.task_id!, mode: published.value ? 'edit' : 'publish' }
}
function publishingChanged(id: string, value: boolean) {
  if (!canPublish.value || publishTarget.value?.song.id !== id || props.song.id !== id) return
  Object.assign(props.song, { is_public: value, visibility: value ? 'public' : 'private', publish_status: value ? 'published' : 'private' })
}
const { liked, busy, failed, toggle } = useSongLike(toRef(props, 'song'))
const items = computed(() => props.song.id ? [
  ...(props.song.audio_url ? [{ id: 'next', label: t('mm.fidelity.playNext'), icon: 'play' as const }] : []),
  ...(!props.inlineActions ? [{ id: 'like', label: t(liked.value ? 'mm.fidelity.removeLiked' : 'mm.fidelity.addLiked'), icon: 'heart' as const, disabled: busy.value }] : []),
  ...(props.showRemix !== false && props.song.remix_allowed !== false ? [{ id: 'similar', label: t('music.createSimilar'), icon: 'wand' as const }] : []),
  { id: 'share', label: t('mm.feed.share'), icon: 'share' as const },
  ...(props.playerMenu ? [{ id: 'stop', label: t('ux.closePlayer'), icon: 'x' as const }] : []),
] : [])
const select = (item: { id: string }) => {
  if (!active.value || !items.value.some(action => action.id === item.id && !('disabled' in action && action.disabled))) return
  if (item.id === 'stop') player.close()
  if (item.id === 'like') void toggle()
  if (item.id === 'share') share.value = true
  if (item.id === 'next') {
    const queue = player.queue.filter(song => song.id !== props.song.id)
    const index = queue.findIndex(song => song.id === player.currentTrack?.id)
    queue.splice(index + 1, 0, props.song)
    player.queue = queue
  }
  if (item.id === 'similar') {
    // CollectionSongs can retain its existing remix handler and analytics.
    if (instance?.vnode.props?.onRemix) { emit('remix', props.song); return }
    saveMusicData({ prompt: props.song.metadata?.prompt || '', tags: props.song.metadata?.tags || '', title: props.song.title || '', lyrics: props.song.metadata?.prompt || '', instrumental: false, mode: 'advanced' }, router.currentRoute.value.path)
    void router.push('/create')
  }
}
</script>
<template>
  <div class="song-menu-actions" :class="{ 'inline-actions': inlineActions }">
    <button v-if="inlineActions && song.id" type="button" class="song-row-action song-row-like" :class="{ 'is-active': liked }" :aria-label="`${t(liked ? 'mm.fidelity.removeLiked' : 'mm.fidelity.addLiked')} · ${song.title || t('copyright.untitled')}`" :aria-pressed="liked" :aria-busy="busy || undefined" :disabled="busy" @click="toggle"><MmIcon name="heart" :size="20" /></button>
    <button v-if="canPublish" type="button" class="song-row-action song-row-publish" :class="{ 'is-active': published }" :aria-label="publishLabel" :title="publishLabel" :aria-pressed="published" @click="openPublishing"><MmIcon name="paperPlane" :size="20" /></button>
    <MmMenu v-if="items.length" v-model="open" :title="song.title || t('copyright.untitled')" :label="t('mm.moreFor', { title: song.title || '' })" :items="items" @select="select">
      <template #trigger="{ toggle: toggleMenu, expanded, id }"><button v-if="inlineActions" type="button" class="song-row-action song-row-more" :aria-label="t('mm.moreFor', { title: song.title || '' })" aria-haspopup="menu" :aria-expanded="expanded" :aria-controls="expanded ? id : undefined" @click="toggleMenu"><MmIcon name="more" :size="20" /></button><MmIconButton v-else icon="more" :size="playerMenu ? 'md' : 'sm'" :icon-size="playerMenu ? 22 : 18" :label="playerMenu ? t('t24a.more') : t('mm.moreFor', { title: song.title || '' })" aria-haspopup="menu" :aria-expanded="expanded" :aria-controls="id" @click="toggleMenu" /></template>
    </MmMenu>
  </div>
  <span v-if="failed" role="alert" class="mm-sr-only">{{ t('common.operation_failed') }}</span>
  <FeedShare :song="share ? song as any : null" @close="share = false" />
  <PublishDialog v-if="publishTarget" :target="publishTarget" @close="publishTarget = null" @changed="publishingChanged" />
</template>

<style scoped>
.song-menu-actions { display: inline-flex; align-items: center; }
.song-row-action { display: grid; place-items: center; width: 44px; height: 44px; flex: 0 0 44px; color: var(--text-3); border-radius: var(--r-pill); }
.song-row-action:hover:not(:disabled) { background: var(--surface-2); color: var(--text); }
.song-row-action:focus-visible { outline: 2px solid var(--accent); outline-offset: -2px; }
.song-row-action:disabled { opacity: .5; }
.song-row-action.is-active { color: var(--accent-text); }
.song-row-like.is-active :deep(svg) { fill: currentColor; }
</style>
